import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { FreeTrialStatus } from '@/services/interfaces/FreeTrial';
import { FreeTrialService } from '@/services/implementations/FreeTrialService';
import { getDeviceIdentifier } from '@/utils/deviceFingerprint';
import { useAuth } from './AuthContext';

interface FreeTrialContextType {
  // 状态
  freeTrialStatus: FreeTrialStatus | null;
  isLoading: boolean;
  error: string | null;
  
  // 是否处于免费试用模式（可以使用试用功能）
  isInFreeTrial: boolean;
  
  // 是否有免费试用状态（包括已结束的试用，用于显示UI）
  hasTrialStatus: boolean;
  
  // 方法
  initializeFreeTrial: () => Promise<void>;
  updateTokenUsage: (tokensUsed: number) => Promise<void>;
  clearFreeTrial: () => void;
}

const FreeTrialContext = createContext<FreeTrialContextType | undefined>(undefined);

export const useFreeTrialContext = () => {
  const context = useContext(FreeTrialContext);
  if (context === undefined) {
    throw new Error('useFreeTrialContext must be used within a FreeTrialProvider');
  }
  return context;
};

interface FreeTrialProviderProps {
  children: React.ReactNode;
}

export const FreeTrialProvider: React.FC<FreeTrialProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [freeTrialStatus, setFreeTrialStatus] = useState<FreeTrialStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);

  const isInFreeTrial = Boolean(
    !isAuthenticated &&
    freeTrialStatus?.isActive && 
    !freeTrialStatus?.isExpired && 
    freeTrialStatus?.remainingTokens > 0
  );

  const hasTrialStatus = Boolean(!isAuthenticated && freeTrialStatus);

  const initializeFreeTrial = useCallback(async () => {
    if (isAuthenticated) {
      console.log('🔑 用户已登录，跳过免费试用初始化');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. 检查是否已有保存的试用ID
      const savedTrialId = FreeTrialService.getSavedTrialId();
      
      if (savedTrialId) {
        // 获取现有试用状态
        const existingStatus = await FreeTrialService.getFreeTrialStatus(savedTrialId);
        if (existingStatus) {
          // 无论试用是否结束，都保存状态以显示相应的UI
          setFreeTrialStatus(existingStatus);
          setIsLoading(false);
          return;
        } else {
          // 只有当无法获取状态时才清除保存的ID
          FreeTrialService.clearSavedTrialId();
        }
      }

      // 2. 生成设备标识符
      const deviceIdentifier = await getDeviceIdentifier();

      // 3. 请求开始新的免费试用
      const response = await FreeTrialService.startFreeTrial({
        deviceId: deviceIdentifier.deviceId,
        fingerprint: deviceIdentifier.fingerprint,
        fingerprintHash: deviceIdentifier.fingerprintHash,
      });

      if (response.success && response.data) {
        const newStatus: FreeTrialStatus = {
          trialId: response.data.trialId,
          remainingTokens: response.data.remainingTokens,
          expiresAt: response.data.expiresAt,
          isActive: true,
          isExpired: false,
        };
        
        setFreeTrialStatus(newStatus);
        FreeTrialService.saveTrialId(response.data.trialId);
        
        if (!response.data.isNewTrial) {
          console.log('Device already used free trial');
        }
      } else {
        // 如果启动试用失败，可能是因为已经用过试用，尝试检查是否有现有的试用记录
        if (response.error?.includes('expired') || response.error?.includes('exhausted')) {
          // 尝试从设备指纹获取可能的试用ID
          console.log('Trial seems to be exhausted, checking for existing trial status');
          // 暂时设置错误信息，让用户知道试用已结束
        } else if (response.errorCode === 'BETA_MODE_ACTIVE') {
          // 内测模式下，不设置错误状态，让 FreeTrialBanner 通过 useBetaMode 来处理显示
          console.log('Beta mode detected, free trial not available');
          setError(null);
          return;
        }
        setError(response.error || 'Failed to initialize free trial');
      }
    } catch (err) {
      console.error('Error initializing free trial:', err);
      setError('Failed to initialize free trial');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const updateTokenUsage = useCallback(async (tokensUsed: number) => {
    if (isAuthenticated) {
      console.log('🔑 用户已登录，跳过免费试用 token 更新');
      return;
    }

    if (!freeTrialStatus?.trialId || !freeTrialStatus?.isActive) {
      return;
    }

    try {
      const response = await FreeTrialService.updateTokenUsage({
        trialId: freeTrialStatus.trialId,
        tokensUsed,
      });

      if (response.success) {
        setFreeTrialStatus(prev => prev ? {
          ...prev,
          remainingTokens: response.remainingTokens,
        } : null);
      } else {
        console.error('Failed to update token usage:', response.error);
      }
    } catch (err) {
      console.error('Error updating token usage:', err);
    }
  }, [freeTrialStatus, isAuthenticated]);

  /**
   * 清除免费试用状态
   */
  const clearFreeTrial = useCallback(() => {
    setFreeTrialStatus(null);
    setError(null);
    
    // 清除localStorage
    FreeTrialService.clearSavedTrialId();
    
    // 关闭定时器
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
    
    console.log('🧹 已清除所有免费试用信息');
  }, [statusCheckInterval]);

  /**
   * 组件挂载时自动初始化
   */
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔑 用户已登录，清除免费试用状态');
      clearFreeTrial();
    }
  }, [isAuthenticated, clearFreeTrial]);

  useEffect(() => {
    if (!isAuthenticated) {
      initializeFreeTrial();
    }
  }, [initializeFreeTrial, isAuthenticated]);

  /**
   * 定期检查试用状态（每分钟）
   */
  useEffect(() => {
    // 清除之前的定时器
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
    }

    if (isAuthenticated || !freeTrialStatus?.trialId) {
      setStatusCheckInterval(null);
      return;
    }

    const trialId = freeTrialStatus.trialId;
    const interval = setInterval(async () => {
      const status = await FreeTrialService.getFreeTrialStatus(trialId);
      if (status) {
        setFreeTrialStatus(status);
      }
    }, 60000);
    
    setStatusCheckInterval(interval);

    return () => {
      clearInterval(interval);
    };
  }, [freeTrialStatus?.trialId, isAuthenticated]);

  const value: FreeTrialContextType = {
    freeTrialStatus,
    isLoading,
    error,
    isInFreeTrial,
    hasTrialStatus,
    initializeFreeTrial,
    updateTokenUsage,
    clearFreeTrial,
  };

  return (
    <FreeTrialContext.Provider value={value}>
      {children}
    </FreeTrialContext.Provider>
  );
}; 